import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BRAND_COLORS,
  COIN_RULES,
  SUBJECTS,
  SUBJECT_IDS,
  type Grade,
  type MeResponse,
  type SubjectId,
} from '@lk/shared';
import { Button } from '../../components/Button';
import { Icon, SUBJECT_ICON } from '../../components/Icon';
import { Panel } from '../../components/Panel';
import { EmptyState, ErrorState, LoadingState } from '../../components/States';
import { cn } from '../../lib/cn';
import { humanMessage } from '../../lib/errors';
import { saveControls, type ControlsPayload } from '../../lib/api';
import { playSound, probeSound } from '../../audio/sound';
import { useSoundStatus } from '../../audio/useSoundStatus';
import { controlsQuery } from '../learner/queries';
import { PasskeyPanel } from '../auth/PasskeyPanel';
import { useUiStore } from '../../store/uiStore';
import { useSelectedLearner } from './ParentLayout';

const WEEKDAYS = ['Нд', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'] as const;

function minutesToTime(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function timeToMinutes(value: string): number {
  const [h = '0', m = '0'] = value.split(':');
  return Math.min(1440, Number(h) * 60 + Number(m));
}

/** Контракт віддає клас як `number` — звужуємо його до Grade в одному місці. */
function toGrade(value: number): Grade {
  return Math.min(7, Math.max(1, Math.round(value))) as Grade;
}

/** Дзеркало DEFAULT_SCHEDULE із сервера: 08:00–15:00 і 16:00–21:00 щодня. */
const DEFAULT_SCHEDULE = [0, 1, 2, 3, 4, 5, 6].flatMap((weekday) => [
  { weekday, startMinute: 8 * 60, endMinute: 15 * 60 },
  { weekday, startMinute: 16 * 60, endMinute: 21 * 60 },
]);

function defaultControls(learnerId: string, grade: number): ControlsPayload {
  return {
    learnerId,
    // Порожній розклад тут показав би батькові «без обмежень», хоча сервер
    // за замовчуванням обмежує. Показуємо те саме, що він застосує.
    schedule: DEFAULT_SCHEDULE,
    dailyMinutesLimit: 0,
    // Ті самі числа, що й DEFAULT_CONTROLS на сервері: 30 хвилин гри,
    // 30 хвилин перерви. Нулі тут показали б батькові «перерв немає»,
    // хоча сервер їх усе одно вимагає.
    sessionMinutes: 30,
    breakMinutes: 30,
    dailyGoalItems: 15,
    dailyCoinCap: COIN_RULES.DEFAULT_DAILY_CAP,
    enabledSubjects: [...SUBJECT_IDS],
    subjectWeightOverride: {},
    grade: toGrade(grade),
  };
}

export function SettingsPage({ me }: { me: MeResponse }): React.JSX.Element {
  const learner = useSelectedLearner(me);
  const queryClient = useQueryClient();
  const soundEnabled = useUiStore((s) => s.soundEnabled);
  const toggleSound = useUiStore((s) => s.toggleSound);
  const reducedMotion = useUiStore((s) => s.reducedMotion);
  const toggleReducedMotion = useUiStore((s) => s.toggleReducedMotion);
  const soundStatus = useSoundStatus();

  const controls = useQuery({
    ...controlsQuery(learner?.id ?? ''),
    enabled: Boolean(learner?.id),
    retry: false,
  });

  const [form, setForm] = useState<ControlsPayload | null>(null);

  useEffect(() => {
    if (soundEnabled) probeSound();
  }, [soundEnabled]);

  useEffect(() => {
    if (!learner) return;
    if (controls.data) setForm(controls.data);
    // Сервер ще не віддав налаштування — показуємо розумні типові,
    // але чесно попереджаємо, що вони ще не збережені.
    else if (controls.isError) setForm(defaultControls(learner.id, learner.grade));
  }, [controls.data, controls.isError, learner]);

  const save = useMutation({
    mutationFn: (payload: ControlsPayload) => saveControls(payload),
    onSuccess: (next) => {
      queryClient.setQueryData(controlsQuery(next.learnerId).queryKey, next);
    },
  });

  if (!learner) return <EmptyState title="Немає дитини для показу" />;
  if (controls.isLoading && !form) return <LoadingState label="Читаємо налаштування…" />;
  if (!form) return <ErrorState error={controls.error} onRetry={() => void controls.refetch()} />;

  const patch = (next: Partial<ControlsPayload>): void => setForm({ ...form, ...next });

  /*
   * Вікон на день може бути кілька, і це не рідкість, а типовий випадок:
   * закрита година з 15:00 до 16:00 розрізає день навпіл. Раніше екран
   * знаходив по дню РІВНО ОДНЕ вікно й на збереженні викидав решту —
   * тобто мовчки скасовував закриту годину, щойно батько чогось торкався.
   *
   * Тому працюємо з індексами в спільному списку, а не з днем як ключем.
   */
  const windowsFor = (weekday: number): Array<{ index: number; startMinute: number; endMinute: number }> =>
    form.schedule
      .map((window, index) => ({ ...window, index }))
      .filter((window) => window.weekday === weekday)
      .sort((a, b) => a.startMinute - b.startMinute);

  const patchWindow = (index: number, next: { startMinute?: number; endMinute?: number }): void =>
    patch({
      schedule: form.schedule.map((window, i) => (i === index ? { ...window, ...next } : window)),
    });

  const removeWindow = (index: number): void =>
    patch({ schedule: form.schedule.filter((_, i) => i !== index) });

  const addWindow = (weekday: number): void => {
    // Продовжуємо день від кінця останнього вікна, щоб нове не накладалося
    // на вже задані. Якщо до півночі вже не влазить — просто типове вечірнє.
    const last = windowsFor(weekday).at(-1);
    const start = last ? Math.min(last.endMinute + 60, 22 * 60) : 16 * 60;
    const end = Math.min(start + 4 * 60, 24 * 60);
    patch({ schedule: [...form.schedule, { weekday, startMinute: start, endMinute: end }] });
  };

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(event) => {
        event.preventDefault();
        save.mutate(form);
      }}
    >
      <Panel className="p-5">
        <h2 className="text-lg font-black">Налаштування · {learner.displayName}</h2>
        {controls.isError ? (
          <p className="mt-2 text-xs font-bold text-[var(--lk-gold)]">
            Сервер ще не віддав збережені налаштування — показуємо типові. Натисни «Зберегти», щоб
            зафіксувати їх.
          </p>
        ) : null}

        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <NumberField
            label="Ліміт хвилин на добу (0 = без обмеження)"
            value={form.dailyMinutesLimit}
            min={0}
            max={600}
            onChange={(value) => patch({ dailyMinutesLimit: value })}
          />
          <NumberField
            label="Хвилин поспіль до перерви (0 = без перерв)"
            value={form.sessionMinutes}
            min={0}
            max={120}
            onChange={(value) => patch({ sessionMinutes: value })}
          />
          <NumberField
            label="Тривалість перерви, хв"
            value={form.breakMinutes}
            min={0}
            max={120}
            onChange={(value) => patch({ breakMinutes: value })}
          />
          <NumberField
            label="Денна ціль, питань"
            value={form.dailyGoalItems}
            min={0}
            max={200}
            onChange={(value) => patch({ dailyGoalItems: value })}
          />
          <NumberField
            label="Денний ліміт балів, грн (0 = без обмеження)"
            value={form.dailyCoinCap}
            min={0}
            max={1000}
            onChange={(value) => patch({ dailyCoinCap: value })}
          />
          <NumberField
            label="Клас"
            value={form.grade ?? learner.grade}
            min={1}
            max={7}
            onChange={(value) => patch({ grade: toGrade(value) })}
          />
        </div>
      </Panel>

      {/*
        Панель ключів стоїть ДРУГОЮ навмисно. Спершу вона була в кінці, перед
        «Цей пристрій», — і на телефоні опинилась на четвертому екрані
        прокрутки, тобто її просто не знаходили. Розклад і предмети правлять
        рідко, а ключ додають один раз і саме тоді, коли шукають.
      */}
      <PasskeyPanel />

      <Panel className="p-5">
        <h3 className="text-base font-black">Розклад доступу</h3>
        <p className="mt-1 text-xs font-semibold text-white/55">
          Порожній розклад = без обмежень. Вікон на день може бути кілька — проміжок
          між ними закритий. Типово це 08:00–15:00 і 16:00–21:00.
        </p>
        <ul className="mt-3 flex flex-col gap-2">
          {WEEKDAYS.map((label, weekday) => {
            const windows = windowsFor(weekday);
            return (
              <li key={label} className="lk-panel-tight flex flex-col gap-2 p-3">
                <div className="flex items-center gap-3">
                  <span className="w-10 text-sm font-black">{label}</span>
                  {windows.length === 0 ? (
                    <span className="text-xs font-bold text-white/40">закрито весь день</span>
                  ) : null}
                  <button
                    type="button"
                    className="lk-chip ml-auto cursor-pointer"
                    onClick={() => addWindow(weekday)}
                    data-testid={`schedule-add-${weekday}`}
                  >
                    + вікно
                  </button>
                </div>

                {windows.map((window, ordinal) => (
                  <div key={window.index} className="flex flex-wrap items-center gap-3 pl-10">
                    <input
                      type="time"
                      value={minutesToTime(window.startMinute)}
                      onChange={(event) =>
                        patchWindow(window.index, { startMinute: timeToMinutes(event.target.value) })
                      }
                      className="lk-panel-tight h-10 px-2 text-sm font-bold outline-none"
                      aria-label={`Початок ${ordinal + 1}, ${label}`}
                    />
                    <span className="text-white/40">—</span>
                    <input
                      type="time"
                      value={minutesToTime(window.endMinute)}
                      onChange={(event) =>
                        patchWindow(window.index, { endMinute: timeToMinutes(event.target.value) })
                      }
                      className="lk-panel-tight h-10 px-2 text-sm font-bold outline-none"
                      aria-label={`Кінець ${ordinal + 1}, ${label}`}
                    />
                    <button
                      type="button"
                      className="lk-chip cursor-pointer"
                      onClick={() => removeWindow(window.index)}
                      aria-label={`Прибрати вікно ${ordinal + 1}, ${label}`}
                    >
                      прибрати
                    </button>
                  </div>
                ))}
              </li>
            );
          })}
        </ul>
      </Panel>

      <Panel className="p-5">
        <h3 className="text-base font-black">Предмети та ручне підсилення</h3>
        <p className="mt-1 text-xs font-semibold text-white/55">
          Вимкнений предмет не потрапляє в сесії. Множник &gt; 1 підсилює предмет поверх
          автоматичного добору.
        </p>
        <ul className="mt-3 flex flex-col gap-2">
          {SUBJECT_IDS.map((subjectId: SubjectId) => {
            const enabled = form.enabledSubjects.includes(subjectId);
            const weight = form.subjectWeightOverride?.[subjectId] ?? 1;
            return (
              <li key={subjectId} className="lk-panel-tight flex flex-wrap items-center gap-3 p-3">
                <button
                  type="button"
                  aria-pressed={enabled}
                  onClick={() =>
                    patch({
                      enabledSubjects: enabled
                        ? form.enabledSubjects.filter((id) => id !== subjectId)
                        : [...form.enabledSubjects, subjectId],
                    })
                  }
                  className={cn(
                    'lk-chip cursor-pointer',
                    enabled
                      ? 'border-[var(--lk-green)] text-[var(--lk-green)]'
                      : 'text-white/40 line-through',
                  )}
                >
                  <Icon name={SUBJECT_ICON[subjectId]} size={16} />
                  {SUBJECTS[subjectId].title}
                </button>
                <div className="flex-1" />
                <label className="flex items-center gap-2 text-xs font-black text-white/60">
                  підсилення
                  <input
                    type="range"
                    min={0}
                    max={3}
                    step={0.25}
                    value={weight}
                    disabled={!enabled}
                    onChange={(event) =>
                      patch({
                        subjectWeightOverride: {
                          ...form.subjectWeightOverride,
                          [subjectId]: Number(event.target.value),
                        },
                      })
                    }
                    aria-label={`Підсилення предмета ${SUBJECTS[subjectId].title}`}
                  />
                  <span
                    className="tabular w-10 text-right"
                    style={{ color: weight > 1 ? BRAND_COLORS.gold : undefined }}
                  >
                    ×{weight.toFixed(2)}
                  </span>
                </label>
              </li>
            );
          })}
        </ul>
      </Panel>

      <Panel className="p-5">
        <h3 className="text-base font-black">Цей пристрій</h3>
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={toggleSound}
            aria-pressed={soundEnabled}
            className={cn('lk-chip cursor-pointer', soundEnabled && 'border-[var(--lk-green)]')}
          >
            {soundEnabled ? 'звук увімкнено' : 'звук вимкнено'}
          </button>
          <button
            type="button"
            onClick={toggleReducedMotion}
            aria-pressed={reducedMotion}
            className={cn('lk-chip cursor-pointer', reducedMotion && 'border-[var(--lk-cyan)]')}
          >
            {reducedMotion ? 'менше анімацій' : 'анімації повні'}
          </button>

          {soundEnabled ? (
            <button
              type="button"
              className="lk-chip cursor-pointer"
              onClick={() => {
                probeSound();
                playSound('tap');
              }}
            >
              перевірити звук
            </button>
          ) : null}
        </div>

        {/*
          Найдовша поломка цього застосунку була саме тихою: реєстр звуків
          існував, файлів не існувало, менеджер чесно ковтав кожну спробу — і
          перемикач місяцями показував «звук увімкнено», не вмикаючи нічого.
          Тепер стан завантаження видно, і саме тут: діагностика — батькам,
          дитина такого не читає.
        */}
        {soundEnabled && soundStatus === 'broken' ? (
          <p className="mt-3 text-xs font-bold text-[var(--lk-gold)]" role="status">
            Звук увімкнено, але жоден файл не завантажився — гра буде тихою. Перевір зв'язок і
            онови сторінку.
          </p>
        ) : null}
      </Panel>

      {save.isError ? (
        <p className="text-sm font-bold text-[var(--lk-red)]" role="alert">
          {humanMessage(save.error)}
        </p>
      ) : null}
      {save.isSuccess ? (
        <p className="text-sm font-bold text-[var(--lk-green)]" role="status">
          Налаштування збережено на сервері.
        </p>
      ) : null}

      <Button type="submit" tone="gold" size="xl" block mute loading={save.isPending}>
        Зберегти
      </Button>
    </form>
  );
}

function NumberField({
  label,
  value,
  min,
  max,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
}): React.JSX.Element {
  return (
    <label className="text-xs font-black tracking-widest text-white/50 uppercase">
      {label}
      <input
        type="number"
        min={min}
        max={max}
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
        className="lk-panel-tight tabular mt-1 h-12 w-full px-3 text-lg font-black outline-none"
      />
    </label>
  );
}
